import { useEnterpriseStore } from '@/modules/enterprise/stores/enterprise.store';
import { ref } from 'vue';
import { getAuthenticatedUserDataService } from '../services/user.service';
import { useUserStore } from '../stores/user.store';
import { USER_DATA_TTL } from '../types/userCache.interface';

/**
 * @description Composable para manejar la revalidación de datos de usuario
 * Implementa estrategia Stale-While-Revalidate con TTL
 * @author Omar Sánchez Martinez
 * @since 10/02/2026
 */
export const useAuthenticatedUserDataRefresh = () => {
  const userStore = useUserStore();
  const enterpriseStore = useEnterpriseStore();
  const isRefreshing = ref(false);

  /**
   * Verifica si el TTL ha expirado
   */
  const isTTLExpired = (): boolean => {
    const user = userStore.user;

    // Si no hay usuario, no hay TTL
    if (!user) return false;

    // Si no tiene metadata, necesita migración
    if (!('metadata' in user)) return true;

    const metadata = user.metadata;
    if (!metadata?.lastFetched || !metadata?.ttl) return true;

    const now = Date.now();
    const elapsed = now - metadata.lastFetched;

    return elapsed >= metadata.ttl;
  };

  /**
   * Refresca los datos del usuario si el TTL ha expirado
   * @returns true si se refrescaron los datos, false si no fue necesario o hubo error
   */
  const refreshIfNeeded = async (): Promise<boolean> => {
    // No refrescar si ya está en proceso
    if (isRefreshing.value) return false;

    // No refrescar si el TTL no ha expirado
    if (!isTTLExpired()) return false;

    try {
      isRefreshing.value = true;

      // Obtener datos frescos del API
      const freshUserData = await getAuthenticatedUserDataService();

      // Actualizar store de usuario con metadata
      const userWithMetadata = {
        ...freshUserData,
        metadata: {
          lastFetched: Date.now(),
          ttl: USER_DATA_TTL,
        },
      };

      userStore.setUser(userWithMetadata);

      // Actualizar activeEnterprise (derivada de user.enterprises)
      const currentEnterpriseId = enterpriseStore.activeEnterprise?.id;
      if (currentEnterpriseId) {
        const updatedEnterprise = freshUserData.enterprises.find(e => e.id === currentEnterpriseId);

        if (updatedEnterprise) {
          enterpriseStore.setActiveEnterprise(updatedEnterprise);
        }
      }

      console.info('[UserDataRefresh] Datos de usuario actualizados exitosamente');
      return true;
    } catch (error) {
      console.error('[UserDataRefresh] Error al refrescar datos:', error);
      return false;
    } finally {
      isRefreshing.value = false;
    }
  };

  /**
   * Fuerza un refresh inmediato (sin validar TTL)
   * Útil para migración de usuarios sin metadata
   * @returns true si se refrescaron los datos, false si hubo error
   */
  const forceRefresh = async (): Promise<boolean> => {
    if (isRefreshing.value) return false;

    try {
      isRefreshing.value = true;
      const freshUserData = await getAuthenticatedUserDataService();

      const userWithMetadata = {
        ...freshUserData,
        metadata: {
          lastFetched: Date.now(),
          ttl: USER_DATA_TTL,
        },
      };

      userStore.setUser(userWithMetadata);

      const currentEnterpriseId = enterpriseStore.activeEnterprise?.id;
      if (currentEnterpriseId) {
        const updatedEnterprise = freshUserData.enterprises.find(e => e.id === currentEnterpriseId);
        if (updatedEnterprise) {
          enterpriseStore.setActiveEnterprise(updatedEnterprise);
        }
      }

      return true;
    } catch (error) {
      console.error('[UserDataRefresh] Error en force refresh:', error);
      return false;
    } finally {
      isRefreshing.value = false;
    }
  };

  return {
    isTTLExpired,
    refreshIfNeeded,
    forceRefresh,
    isRefreshing,
  };
};
